/* =============================================================================
 * Tusq Apparel — Phase 1 Analytics Event Handlers
 * -----------------------------------------------------------------------------
 * Companion JS for snippets/tusq-analytics-events.liquid.
 * Pushes runtime events into the existing GTM container (GTM-TTFXJWVZ):
 *   • add_to_cart          — Dawn pub/sub cartUpdate event
 *   • remove_from_cart     — cart drawer / cart page remove clicks
 *   • variant_change       — Dawn pub/sub variantChange event (updates context)
 *   • select_item          — clicks on product cards (PDP link)
 *   • search (input)       — search form submits (header search + main-search)
 *   • form_start           — focus on any tracked form input
 *   • form_submit          — submit on tracked forms (contact, newsletter, etc.)
 *   • click                — outbound + tel/mailto clicks (auto-augment enhanced
 *                            measurement with form_name + link_classes)
 * Depends on window.tusqAnalytics (set by the Liquid snippet) and PUB_SUB_EVENTS
 * (set by assets/constants.js). Loaded once per page, defer'd.
 * ============================================================================= */
(function () {
  'use strict';

  // Defensive: dataLayer and tusqAnalytics MUST exist (set by snippet inline).
  window.dataLayer = window.dataLayer || [];
  window.tusqAnalytics = window.tusqAnalytics || { currency: 'USD', fired: {} };

  function push(event, payload) {
    try {
      window.dataLayer.push({ ecommerce: null }); // clear before each ecommerce push
      window.dataLayer.push(Object.assign({ event: event }, payload));
    } catch (e) {
      if (window.console && console.warn) console.warn('[tusq-analytics]', event, e);
    }
  }

  function pushNonEcom(event, payload) {
    try {
      window.dataLayer.push(Object.assign({ event: event }, payload));
    } catch (e) {
      if (window.console && console.warn) console.warn('[tusq-analytics]', event, e);
    }
  }

  // ---------------------------------------------------------------------------
  // 1. add_to_cart  —  subscribe to Dawn cartUpdate pub/sub
  // ---------------------------------------------------------------------------
  function handleCartUpdate(payload) {
    if (!payload || payload.source !== 'product-form') return;
    var variantId = String(payload.productVariantId || '');
    var ctx = window.tusqAnalytics || {};
    var variant = (ctx.variants && ctx.variants[variantId]) || null;
    var product = ctx.product || {};

    // Quantity: read from the matching form's input if present, else 1
    var qty = 1;
    try {
      var qInput = document.querySelector('product-form input[name="quantity"]');
      if (qInput && qInput.value) qty = Number(qInput.value) || 1;
    } catch (e) {}

    var price = variant ? variant.price : 0;
    push('add_to_cart', {
      ecommerce: {
        currency: ctx.currency,
        value: +(price * qty).toFixed(2),
        items: [{
          item_id: String(product.product_id || ''),
          item_name: product.product_name || '',
          item_brand: product.brand || '',
          item_category: product.category || '',
          item_variant: variant ? variant.variant_title : '',
          item_variant_sku: variant ? variant.variant_sku : '',
          price: price,
          quantity: qty
        }]
      }
    });
  }

  // ---------------------------------------------------------------------------
  // 2. variant_change  —  subscribe to Dawn variantChange pub/sub
  // ---------------------------------------------------------------------------
  function handleVariantChange(payload) {
    if (!payload) return;
    var variant = payload.variant || {};
    var ctx = window.tusqAnalytics || {};
    var product = ctx.product || {};

    // Keep the runtime context in sync so next ATC has the right SKU
    if (ctx.variants && variant.id && ctx.variants[String(variant.id)]) {
      ctx.current_variant_id = String(variant.id);
    }

    pushNonEcom('variant_change', {
      item_id: String(product.product_id || ''),
      item_name: product.product_name || '',
      item_variant: variant.title || '',
      item_variant_sku: variant.sku || '',
      price: variant.price ? +(variant.price / 100).toFixed(2) : 0
    });
  }

  // ---------------------------------------------------------------------------
  // 3. remove_from_cart  —  delegate clicks on cart remove buttons
  // ---------------------------------------------------------------------------
  function handleRemoveClick(e) {
    var target = e.target.closest('cart-remove-button a, a.cart-remove-button, [data-cart-remove]');
    if (!target) return;

    var line = target.closest('[data-line]') || target.closest('.cart-item') || target.closest('tr');
    if (!line) return;

    var variantId = line.getAttribute('data-variant-id') || line.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') || '';
    var productId = line.getAttribute('data-product-id') || line.querySelector('[data-product-id]')?.getAttribute('data-product-id') || '';
    var title = (line.querySelector('.cart-item__name, [data-product-title]') || {}).textContent || '';
    var sku = (line.querySelector('[data-variant-sku]') || {}).textContent || '';
    var priceTxt = (line.querySelector('.cart-item__price, [data-product-price]') || {}).textContent || '';
    var price = parseFloat(String(priceTxt).replace(/[^0-9.]/g, '')) || 0;
    var qty = parseInt((line.querySelector('input[name="updates[]"], [data-quantity]') || {}).value, 10) || 1;

    push('remove_from_cart', {
      ecommerce: {
        currency: window.tusqAnalytics.currency,
        value: +(price * qty).toFixed(2),
        items: [{
          item_id: String(productId),
          item_name: title.trim(),
          item_variant_sku: sku.trim(),
          price: price,
          quantity: qty
        }]
      }
    });
  }

  // ---------------------------------------------------------------------------
  // 4. select_item  —  clicks on product cards (links to PDP)
  // ---------------------------------------------------------------------------
  function handleCardClick(e) {
    var link = e.target.closest('.card-wrapper a[href*="/products/"], .card__inner a[href*="/products/"], .tusq-card-product a[href*="/products/"]');
    if (!link) return;

    var card = link.closest('[data-product-id], .card-wrapper, .card');
    if (!card) return;

    var productId = card.getAttribute('data-product-id') || (card.querySelector('[data-product-id]') || {}).getAttribute?.('data-product-id') || '';
    var variantId = card.getAttribute('data-variant-id') || (card.querySelector('[data-variant-id]') || {}).getAttribute?.('data-variant-id') || '';
    var nameEl = card.querySelector('.card__heading, .card__information .card__heading a, [data-product-title]');
    var name = nameEl ? nameEl.textContent.trim() : '';
    var listName = '';
    var listIdEl = link.closest('[data-list-name]');
    if (listIdEl) listName = listIdEl.getAttribute('data-list-name');
    else if (document.body.classList.contains('template-collection')) {
      var t = document.querySelector('.collection-hero__title, .collection__title');
      if (t) listName = t.textContent.trim();
    }

    pushNonEcom('select_item', {
      ecommerce: {
        item_list_name: listName,
        items: [{
          item_id: String(productId),
          item_name: name,
          item_variant: '',
          item_variant_sku: ''
        }]
      }
    });
  }

  // ---------------------------------------------------------------------------
  // 5. search  —  submit on search forms
  // ---------------------------------------------------------------------------
  function handleSearchSubmit(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!form.matches('form[action="/search"], form.search, form[role="search"]')) return;

    var input = form.querySelector('input[name="q"], input[type="search"]');
    if (!input || !input.value) return;

    pushNonEcom('search', {
      search_term: input.value.trim(),
      search_source: form.id || form.className || 'unknown'
    });
  }

  // ---------------------------------------------------------------------------
  // 6. form_start / form_submit  —  generic form tracking with form_name
  // ---------------------------------------------------------------------------
  function nameForForm(form) {
    return form.getAttribute('data-form-name')
      || form.id
      || form.getAttribute('name')
      || form.className.split(' ')[0]
      || 'unknown_form';
  }
  var formStartFired = new WeakSet();
  function handleFormFocus(e) {
    var form = e.target.closest('form');
    if (!form) return;
    // Skip search forms (handled separately) and ATC product-form (handled via cartUpdate)
    if (form.matches('form[action="/search"], form.search, form[role="search"]')) return;
    if (form.matches('product-form form, form[action*="/cart/add"]')) return;
    if (formStartFired.has(form)) return;
    formStartFired.add(form);
    pushNonEcom('form_start', {
      form_name: nameForForm(form),
      form_id: form.id || '',
      form_destination: form.action || ''
    });
  }
  function handleFormSubmit(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (form.matches('form[action="/search"], form.search, form[role="search"]')) return;
    if (form.matches('product-form form, form[action*="/cart/add"]')) return;
    pushNonEcom('form_submit', {
      form_name: nameForForm(form),
      form_id: form.id || '',
      form_destination: form.action || ''
    });
  }

  // ---------------------------------------------------------------------------
  // 7. outbound / tel / mailto clicks  —  augment enhanced measurement
  // ---------------------------------------------------------------------------
  function handleAnyClick(e) {
    var a = e.target.closest('a[href]');
    if (!a) return;
    var href = a.getAttribute('href') || '';
    if (!href) return;
    if (href.indexOf('mailto:') === 0) {
      pushNonEcom('email_click', { link_url: href });
      return;
    }
    if (href.indexOf('tel:') === 0) {
      pushNonEcom('phone_click', { link_url: href });
      return;
    }
    try {
      var url = new URL(a.href, window.location.href);
      if (url.hostname && url.hostname !== window.location.hostname && url.hostname !== 'www.' + window.location.hostname) {
        pushNonEcom('outbound_click', {
          link_domain: url.hostname,
          link_url: a.href,
          link_classes: a.className || ''
        });
      }
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Wiring — subscribe + delegate
  // ---------------------------------------------------------------------------
  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
      subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChange);
    }
    document.addEventListener('click', function (e) {
      handleRemoveClick(e);
      handleCardClick(e);
      handleAnyClick(e);
    }, { capture: true });
    document.addEventListener('submit', function (e) {
      handleSearchSubmit(e);
      handleFormSubmit(e);
    }, { capture: true });
    document.addEventListener('focusin', handleFormFocus, { capture: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
